/**
 * FO-432 (batch-59) - interne (intercompany) INKOOPfacturen: patroonherkenning
 * per BV-paar en herhaal-regels die per periode een intern concept klaarzetten.
 *
 * Afbakening t.o.v. FO-419 (batch-55): die batch bouwde de HERKENNING van een
 * factuur tussen eigen BV's (`InkoopFactuur.isIntercompany` +
 * `intercompanyTegenpartijEntityId`) en de patroon-/concept-machinerie aan de
 * VERKOOPkant (`IntercompanyPatroon` op `Invoice`). Dit bestand hoort bij de
 * KOSTENkant: de factuur komt binnen op de inkoop-mailbox van de ontvangende
 * BV, en de herhaal-regel zet daar per periode een concept klaar met status
 * `KLAARGEZET` (telt nergens mee tot een mens bevestigt).
 *
 * Geld: alle bedragen reizen als canonieke decimale string (2 decimalen) of als
 * hele centen (integer). Nooit als float.
 */

import { z } from 'zod'

// ─── Vocabulaire ─────────────────────────────────────────────────────────────

/** Ritme van een herhaal-regel (mirror van Prisma-enum `InterneHerhaalRitme`). */
export const INTERNE_HERHAAL_RITME = ['MAANDELIJKS', 'VIER_WEKEN', 'KWARTAAL'] as const
export type InterneHerhaalRitme = (typeof INTERNE_HERHAAL_RITME)[number]

export const INTERNE_HERHAAL_RITME_LABELS: Record<InterneHerhaalRitme, string> = {
  MAANDELIJKS: 'Maandelijks',
  VIER_WEKEN: 'Elke 4 weken',
  KWARTAAL: 'Per kwartaal',
}

/** Zelfstandig naamwoord voor "per {periode}" in kopij ("per maand"). */
export const INTERNE_HERHAAL_RITME_PERIODE_LABELS: Record<InterneHerhaalRitme, string> = {
  MAANDELIJKS: 'maand',
  VIER_WEKEN: '4 weken',
  KWARTAAL: 'kwartaal',
}

/** Status van een herhaal-regel (mirror van Prisma-enum `InterneHerhaalStatus`). */
export const INTERNE_HERHAAL_STATUS = [
  'VOORGESTELD',
  'ACTIEF',
  'GEPAUZEERD',
  'AFGEWEZEN',
  'GESTOPT',
] as const
export type InterneHerhaalStatus = (typeof INTERNE_HERHAAL_STATUS)[number]

export const INTERNE_HERHAAL_STATUS_LABELS: Record<InterneHerhaalStatus, string> = {
  VOORGESTELD: 'Voorgesteld',
  ACTIEF: 'Actief',
  GEPAUZEERD: 'Gepauzeerd',
  AFGEWEZEN: 'Afgewezen',
  GESTOPT: 'Gestopt',
}

export const INTERNE_HERHAAL_STATUS_BADGE_VARIANT: Record<
  InterneHerhaalStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  VOORGESTELD: 'info',
  ACTIEF: 'success',
  GEPAUZEERD: 'warning',
  AFGEWEZEN: 'secondary',
  GESTOPT: 'secondary',
}

/**
 * Waar het bedrag van het klaargezette concept vandaan komt: een vast
 * referentiebedrag, of het bedrag van de laatst geboekte interne factuur van
 * hetzelfde paar + dezelfde omschrijvingssleutel.
 */
export const INTERNE_HERHAAL_BEDRAG_BRON = ['VAST', 'LAATSTE_FACTUUR'] as const
export type InterneHerhaalBedragBron = (typeof INTERNE_HERHAAL_BEDRAG_BRON)[number]

export const INTERNE_HERHAAL_BEDRAG_BRON_LABELS: Record<InterneHerhaalBedragBron, string> = {
  VAST: 'Vast bedrag',
  LAATSTE_FACTUUR: 'Bedrag van de laatste factuur',
}

// ─── Drempels (spec §6, aanname A2) ──────────────────────────────────────────

/**
 * Defaults van de patroon-analyse. `bedragTolerantiePct` is per herhaal-regel
 * instelbaar; de overige drempels gelden voor de dagelijkse analyse.
 */
export const INTERNE_PATROON_DREMPELS = {
  /** Minimaal aantal opeenvolgende, passende periodes vóór een voorstel. */
  minHerkenningen: 3,
  /** Toegestane afwijking (%) rond de mediaan om als "vergelijkbaar bedrag" te gelden. */
  bedragTolerantiePct: 5,
  /** Speling (dagen) op het interval tussen twee opeenvolgende facturen. */
  ritmeTolerantieDagen: 7,
  /** Hoe ver (maanden) de analyse terugkijkt. */
  terugkijkMaanden: 14,
  /** Aantal opeenvolgende onbevestigde periodes waarna de regel automatisch pauzeert. */
  pauzeerNaOnbevestigdePeriodes: 2,
} as const

// ─── Kopij ───────────────────────────────────────────────────────────────────

/** Badge-tekst op een interne (intercompany) inkoopfactuur: "Intern - A -> B". */
export function formatInterneStroomBadge(
  afzenderNaam: string | null | undefined,
  ontvangerNaam: string | null | undefined,
): string {
  return `Intern - ${afzenderNaam?.trim() || 'onbekende BV'} -> ${ontvangerNaam?.trim() || 'onbekende BV'}`
}

/** Badge-tekst op een klaargezet concept dat nog op bevestiging wacht. */
export const INTERNE_KLAARGEZET_BADGE = 'Klaargezet - wacht op bevestiging'

/** Uitleg bij een geweigerde A -> A-herkenning (factuur van een BV aan zichzelf). */
export function interneZelfdeBvUitleg(bvNaam: string): string {
  return (
    `Niet als interne stroom geboekt: afzender en ontvanger zijn allebei ${bvNaam}. ` +
    'Een BV kan zichzelf niet factureren; corrigeer de leverancier of de ontvangende BV.'
  )
}

/**
 * Hint wanneer de leverancier op een eigen BV LIJKT maar de match niet
 * eenduidig is (KvK ontbreekt of wijst een andere BV aan). De factuur volgt het
 * gewone externe pad; er wordt nooit stil intern geboekt.
 */
export function interneMogelijkInternHint(bvNaam: string): string {
  return (
    `Mogelijk intern: de leverancier lijkt op de eigen BV ${bvNaam}, maar de match is niet ` +
    'eenduidig (KvK ontbreekt of wijst een andere BV aan). Deze factuur volgt het gewone ' +
    "(externe) pad; markeer 'm handmatig als factuur tussen eigen BV's als dat wel klopt."
  )
}

/** Hint wanneer de leverancier wél een eigen BV is maar de ontvangende BV niet vaststaat. */
export function interneOntvangerOnbekendHint(bvNaam: string): string {
  return (
    `Mogelijk intern: de leverancier is herkend als eigen BV (${bvNaam}), maar de ontvangende ` +
    'BV is niet vastgesteld. Deze factuur volgt het gewone (externe) pad; kies de ontvangende ' +
    "BV en markeer 'm als factuur tussen eigen BV's als dat klopt."
  )
}

/** Hint bij een klaargezet concept waarvoor alsnog een mail-exemplaar binnenkomt. */
export function interneSamenvoegHint(periodeLabel: string, factuurNr: string): string {
  return (
    `Mogelijk dubbel: er staat al een klaargezet intern concept voor ${periodeLabel} ` +
    `(${factuurNr}). Voeg samen zodat er één boeking overblijft.`
  )
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface InterneBvRefDTO {
  id: string
  name: string
}

/** Beoordeling van één periode van een herhaal-regel (op lezen berekend). */
export const INTERNE_PERIODE_STATUS = [
  'GEBOEKT',
  'KLAARGEZET',
  'ONTBREEKT',
  'VERWACHT',
] as const
export type InternePeriodeStatus = (typeof INTERNE_PERIODE_STATUS)[number]

export const INTERNE_PERIODE_STATUS_LABELS: Record<InternePeriodeStatus, string> = {
  GEBOEKT: 'Geboekt',
  KLAARGEZET: 'Klaargezet',
  ONTBREEKT: 'Blijft uit',
  VERWACHT: 'Verwacht',
}

export const INTERNE_PERIODE_STATUS_BADGE_VARIANT: Record<
  InternePeriodeStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  GEBOEKT: 'success',
  KLAARGEZET: 'info',
  ONTBREEKT: 'warning',
  VERWACHT: 'secondary',
}

export interface InternePeriodeDTO {
  /** Machine-sleutel: `YYYY-MM`, `YYYY-Pnn` (4 weken) of `YYYY-Qn`. */
  periode: string
  label: string
  status: InternePeriodeStatus
  factuurId: string | null
  factuurNr: string | null
  /** Totaalbedrag excl. btw als decimale string, of null. */
  bedragExcl: string | null
}

/** Eén herhaal-regel in de lijst. */
export interface InterneHerhaalRegelRowDTO {
  id: string
  status: InterneHerhaalStatus
  ritme: InterneHerhaalRitme
  afzender: InterneBvRefDTO
  ontvanger: InterneBvRefDTO
  leverancier: { id: string; name: string } | null
  bedragBron: InterneHerhaalBedragBron
  bedragExcl: string
  btwPercentage: string
  bedragTolerantiePct: string
  omschrijving: string | null
  dagVanPeriode: number
  herkendAantal: number
  laatsteConceptPeriode: string | null
  onbevestigdeReeks: number
  voorgesteldOp: string
  geaccepteerdOp: string | null
  gepauzeerdOp: string | null
  gepauzeerdReden: string | null
  gestoptOp: string | null
  stopReden: string | null
  /** Per BV-paar: staat "niet meer vragen" aan? */
  nietMeerVragen: boolean
  /** Aantal facturen dat aan deze regel gekoppeld is. */
  factuurAantal: number
  /** Lopende periode (laatste element van `periodes`), of null. */
  huidigePeriode: InternePeriodeDTO | null
}

export interface InterneHerhaalRegelDetailDTO extends InterneHerhaalRegelRowDTO {
  periodes: InternePeriodeDTO[]
  drempels: typeof INTERNE_PATROON_DREMPELS
}

export interface InterneHerhaalRegelListMeta {
  total: number
  page: number
  pageSize: number
  perStatus: Record<InterneHerhaalStatus, number>
}

/**
 * Eén BV-paar in het intercompany-overzicht van de INKOOPstroom
 * (`GET /inkoop/herhaal-regels/overzicht`).
 *
 * De bedragen zijn de tegenhanger van de externe crediteuren-cijfers waar deze
 * facturen juist NIET in staan (spec §B4). Klaargezette concepten tellen bewust
 * niet mee in `geboektTotaal`/`openTotaal`: ze staan apart als
 * `klaargezetAantal`, want tot bevestiging telt zo'n concept nergens.
 */
export interface InterneStroomPaarDTO {
  afzender: InterneBvRefDTO
  ontvanger: InterneBvRefDTO
  /** Geboekt = GOEDGEKEURD of BETAALD. */
  geboektAantal: number
  geboektTotaal: string
  /** Open = ONTVANGEN, IN_BEHANDELING of BETWIST (nog te controleren/behandelen). */
  openAantal: number
  openTotaal: string
  /** Klaargezette concepten die nog op bevestiging wachten (tellen niet in de bedragen). */
  klaargezetAantal: number
  // batch-66 interne-geldstroom (FO-494) - de omzetkant + het eenzijdigheids-signaal.
  /** Uitgegeven intercompany-verkoopfacturen van de afzender aan de ontvanger. */
  verkoopAantal: number
  /** Som incl. btw van die verkoopfacturen (zelfde grondslag als `geboektTotaal`). */
  verkoopTotaal: string
  /** Geboekte inkoopfacturen zonder spiegel-verkoopfactuur. */
  inkoopZonderVerkoop: number
  /** Verkoopfacturen zonder inkoopfactuur. */
  verkoopZonderInkoop: number
  /** Verkoop min inkoop (geboekt), incl. btw. */
  kantenVerschil: string
  /** Er is iets eenzijdig geboekt, of de twee kanten tellen niet gelijk op. */
  eenzijdig: boolean
}

/** Antwoord van GET /inkoop/herhaal-regels/overzicht. */
export interface InterneStroomOverzichtDTO {
  paren: InterneStroomPaarDTO[]
  /** Van wanneer de cijfers zijn (ISO-instant). */
  peilmoment: string
}

/** Antwoord van POST /inkoop/herhaal-regels/analyse. */
export interface InternePatroonAnalyseResultDTO {
  voorgesteld: number
  parenOnderzocht: number
  overgeslagenNietMeerVragen: number
}

/** Antwoord van POST /inkoop/herhaal-regels/:id/concept-nu. */
export interface InterneConceptResultDTO {
  periode: string
  factuurId: string | null
  alAanwezig: boolean
}

/** Antwoord van POST /inkoop/facturen/:id/samenvoegen. */
export interface InterneSamenvoegResultDTO {
  /** Het overgebleven (klaargezette) concept. */
  factuurId: string
  factuurNr: string
  /** Het samengevoegde mail-exemplaar (soft-deleted). */
  samengevoegdVanId: string
  /** Aantal verplaatste bijlagen. */
  bijlagen: number
}

/** Samenvoeg-kandidaat op het factuurdetail (mail-exemplaar <-> klaargezet concept). */
export interface InterneSamenvoegKandidaatDTO {
  factuurId: string
  factuurNr: string
  periode: string
  periodeLabel: string
  bedragExcl: string
  totaal: string
  /** 'KLAARGEZET' = de kandidaat is het klaargezette concept (deze factuur is het mail-exemplaar). */
  rol: 'KLAARGEZET' | 'MAIL'
}

// ─── Zod-schema's (gedeeld API <-> form) ─────────────────────────────────────

const bedragString = z
  .string()
  .trim()
  .regex(/^-?\d{1,10}([.,]\d{1,2})?$/, 'Vul een bedrag in, bijvoorbeeld 1234,56')

export const interneHerhaalRegelListQuerySchema = z.object({
  status: z.enum(INTERNE_HERHAAL_STATUS).optional(),
  afzenderEntityId: z.string().min(1).optional(),
  ontvangerEntityId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type InterneHerhaalRegelListQuery = z.infer<typeof interneHerhaalRegelListQuerySchema>

/** Body van POST /inkoop/herhaal-regels/:id/accepteren ("ja"). */
export const interneHerhaalAccepterenSchema = z.object({
  ritme: z.enum(INTERNE_HERHAAL_RITME).optional(),
  dagVanPeriode: z.coerce.number().int().min(1).max(28).optional(),
  bedragBron: z.enum(INTERNE_HERHAAL_BEDRAG_BRON).optional(),
  bedragTolerantiePct: z.coerce.number().min(0).max(100).optional(),
})
export type InterneHerhaalAccepterenInput = z.infer<typeof interneHerhaalAccepterenSchema>

/** Body van de reden-acties (afwijzen / pauzeren / stoppen / niet meer vragen). */
export const interneHerhaalRedenSchema = z.object({
  reden: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type InterneHerhaalRedenInput = z.infer<typeof interneHerhaalRedenSchema>

/** Body van PATCH /inkoop/herhaal-regels/:id (bedrag/ritme/omschrijving wijzigen). */
export const interneHerhaalUpdateSchema = z
  .object({
    ritme: z.enum(INTERNE_HERHAAL_RITME).optional(),
    bedragBron: z.enum(INTERNE_HERHAAL_BEDRAG_BRON).optional(),
    bedragExcl: bedragString.optional(),
    btwPercentage: z.coerce.number().min(0).max(100).optional(),
    bedragTolerantiePct: z.coerce.number().min(0).max(100).optional(),
    dagVanPeriode: z.coerce.number().int().min(1).max(28).optional(),
    omschrijving: z.string().trim().max(300, 'Maximaal 300 tekens').nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Geef minstens één veld om te wijzigen' })
export type InterneHerhaalUpdateInput = z.infer<typeof interneHerhaalUpdateSchema>

/** Body van POST /inkoop/herhaal-regels/paar/niet-meer-vragen. */
export const interneNietMeerVragenSchema = z.object({
  afzenderEntityId: z.string().min(1, 'Kies de verzendende BV'),
  ontvangerEntityId: z.string().min(1, 'Kies de ontvangende BV'),
  /** false = het paar weer laten voorstellen (terugzetten in de instellingen). */
  nietMeerVragen: z.boolean(),
  reden: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type InterneNietMeerVragenInput = z.infer<typeof interneNietMeerVragenSchema>

/** Body van POST /inkoop/facturen/:id/samenvoegen (id = het mail-exemplaar). */
export const interneSamenvoegSchema = z.object({
  /** Het klaargezette concept waarin het mail-exemplaar opgaat. */
  doelFactuurId: z.string().min(1, 'Kies het klaargezette concept'),
  reden: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type InterneSamenvoegInput = z.infer<typeof interneSamenvoegSchema>
